/* eslint-disable no-console */
import React, { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { getSaldoCredito } from "../../../utils";
import { LOCALE_POR_PAIS } from "../constants";

/**
 * Genera rango de meses entre inicio y fin (inclusive).
 */
function generateMonthRange(startMonth, startYear, endMonth, endYear) {
  const months = [];
  let m = startMonth;
  let y = startYear;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    months.push({ month: m, year: y });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return months;
}

const MES_LABELS = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

function MonthYearPicker({ value, onChange, isInvalid, allowPast = false }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [viewYear, setViewYear] = useState(() => (value ? parseInt(value.split("-")[0]) : new Date().getFullYear()));
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12

  const selMonth = value ? parseInt(value.split("-")[1]) : null;
  const selYear = value ? parseInt(value.split("-")[0]) : null;
  const display = value ? `${MES_LABELS[selMonth - 1]} ${String(selYear).slice(2)}` : "";

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open]);

  useEffect(() => {
    if (value) setViewYear(parseInt(value.split("-")[0]));
  }, [value]);

  const getPos = () => {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const r = triggerRef.current.getBoundingClientRect();
    return { top: r.bottom + 4, left: r.left };
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`form-control ${isInvalid ? "is-invalid" : ""}`}
        onClick={() => setOpen(!open)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          userSelect: "none",
        }}
      >
        {display ? <span style={{ fontWeight: 500 }}>{display}</span> : <span className="text-muted">Mes...</span>}
        <span style={{ fontSize: "0.6rem", color: "#6c757d" }}>▼</span>
      </div>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: getPos().top,
              left: getPos().left,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "10px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
              padding: "16px",
              minWidth: "270px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              <span
                onClick={() => setViewYear((y) => Math.max(2025, y - 1))}
                style={{
                  cursor: viewYear > 2025 ? "pointer" : "default",
                  padding: "2px 10px",
                  color: viewYear > 2025 ? "#212529" : "#ced4da",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                ‹
              </span>
              <strong style={{ fontSize: "1.05rem" }}>{viewYear}</strong>
              <span
                onClick={() => setViewYear((y) => Math.min(2028, y + 1))}
                style={{
                  cursor: viewYear < 2028 ? "pointer" : "default",
                  padding: "2px 10px",
                  color: viewYear < 2028 ? "#212529" : "#ced4da",
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  userSelect: "none",
                }}
              >
                ›
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
              {MES_LABELS.map((label, i) => {
                const mn = i + 1;
                const isSel = selMonth === mn && selYear === viewYear;
                const isPast =
                  !allowPast && (viewYear < currentYear || (viewYear === currentYear && mn < currentMonth));
                return (
                  <div
                    key={label}
                    onClick={() => {
                      if (isPast) return;
                      onChange(`${viewYear}-${String(mn).padStart(2, "0")}`);
                      setOpen(false);
                    }}
                    onMouseEnter={() => !isPast && setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      textAlign: "center",
                      padding: "10px 4px",
                      borderRadius: "8px",
                      cursor: isPast ? "default" : "pointer",
                      fontSize: "0.85rem",
                      fontWeight: isSel ? 600 : 400,
                      background: isSel ? "#0d6efd" : hovered === i && !isPast ? "#f0f4ff" : "transparent",
                      color: isPast ? "#ced4da" : isSel ? "#fff" : "#212529",
                      transition: "background 0.12s",
                    }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Modal para vincular una oportunidad nueva al cotizador.
 * Soporta deals normales, multi-mes y audiencia de referencia.
 */
export function ModalVincular({
  showLinkModal,
  setShowLinkModal,
  clientesLoading,
  handleSubmitLink,
  registerLink,
  linkErrors,
  setLinkValue,
  handleLinkOportunidad,
  generatePdfDocument,
  generateFileName,
  tipoCotizacion,
  setTipoCotizacion,
  renderClientesOptions,
  orderedMeses,
  isSubmitting,
  canLinkOpportunity,
  // Nuevos props para contratos
  watchLink,
  inversionFinalTotal,
  finalAmount,
  manualDiscountAmount,
  audienciaSeleccionadaTotal,
  pais,
  plazo,
  clientes,
  forceDollars,
}) {
  const ls = LOCALE_POR_PAIS[pais] || "es-CL";

  // --- Crédito del cliente seleccionado ---
  const [creditoCliente, setCreditoCliente] = useState(null);
  const [creditoLoading, setCreditoLoading] = useState(false);
  const clienteSeleccionado = watchLink ? watchLink("clientePublicitario") : "";

  useEffect(() => {
    if (!clienteSeleccionado || !clientes) {
      setCreditoCliente(null);
      return;
    }
    const selectedClient = clientes.find((c) => c.nombre === clienteSeleccionado);
    if (!selectedClient) {
      setCreditoCliente(null);
      return;
    }
    let cancelled = false;
    setCreditoLoading(true);
    getSaldoCredito(selectedClient.id)
      .then((saldos) => {
        if (!cancelled) setCreditoCliente(Object.keys(saldos).length > 0 ? saldos : null);
      })
      .catch(() => {
        if (!cancelled) setCreditoCliente(null);
      })
      .finally(() => {
        if (!cancelled) setCreditoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clienteSeleccionado, clientes]);
  const isMultiMonth = watchLink ? watchLink("is_multi_month") : false;
  const isAudienciaRef = watchLink ? watchLink("audiencia_referencia") : false;
  const distribucionTipo = watchLink ? watchLink("distribucion_tipo") || "prorrateo" : "prorrateo";
  const plazoModo = watchLink ? watchLink("plazo_modo") || "mes_tipo" : "mes_tipo";
  const startMonth = watchLink ? watchLink("multi_month_start_month") : "";
  const startYear = watchLink ? watchLink("multi_month_start_year") : "";
  const endMonth = watchLink ? watchLink("multi_month_end_month") : "";
  const endYear = watchLink ? watchLink("multi_month_end_year") : "";
  const mesCierre = watchLink ? watchLink("mes_cierre") : "";
  const anoCierre = watchLink ? watchLink("ano_cierre") : "";
  const mesFactura = watchLink ? watchLink("mes_factura") : "";
  const anoFactura = watchLink ? watchLink("ano_factura") : "";
  const pautaStartMonth = watchLink ? watchLink("pauta_start_month") : "";
  const pautaStartYear = watchLink ? watchLink("pauta_start_year") : "";
  const pautaEndMonth = watchLink ? watchLink("pauta_end_month") : "";
  const pautaEndYear = watchLink ? watchLink("pauta_end_year") : "";

  const isContrato = isMultiMonth || isAudienciaRef;
  const totalImporte = manualDiscountAmount > 0 ? finalAmount : inversionFinalTotal;

  // Calcular preview de prorrateo
  const prorateoPreview = useMemo(() => {
    if (!isMultiMonth || !startMonth || !startYear || !endMonth || !endYear) return null;

    const sm = parseInt(startMonth);
    const sy = parseInt(startYear);
    const em = parseInt(endMonth);
    const ey = parseInt(endYear);
    if (isNaN(sm) || isNaN(sy) || isNaN(em) || isNaN(ey)) return null;

    const range = generateMonthRange(sm, sy, em, ey);
    if (range.length < 2) return null;

    return plazoModo === "mes_tipo"
      ? {
          months: range.length,
          perMonth: Math.floor(totalImporte),
          grandTotal: Math.floor(totalImporte) * range.length,
          mode: "mes_tipo",
        }
      : {
          months: range.length,
          perMonth: Math.floor(totalImporte / range.length),
          grandTotal: Math.floor(totalImporte),
          mode: "total",
        };
  }, [isMultiMonth, startMonth, startYear, endMonth, endYear, totalImporte, plazoModo]);

  const [showMonthsRequired, setShowMonthsRequired] = useState(false);

  // Validar rango de meses
  const rangeError = useMemo(() => {
    if (!isMultiMonth || !startMonth || !startYear || !endMonth || !endYear) return null;

    const sm = parseInt(startMonth);
    const sy = parseInt(startYear);
    const em = parseInt(endMonth);
    const ey = parseInt(endYear);
    if (isNaN(sm) || isNaN(sy) || isNaN(em) || isNaN(ey)) return null;

    const startDate = new Date(sy, sm - 1);
    const endDate = new Date(ey, em - 1);
    if (endDate <= startDate) return "Fecha fin debe ser posterior a fecha inicio";

    const range = generateMonthRange(sm, sy, em, ey);
    if (range.length < 2) return "Mínimo 2 meses para acuerdo multi-mes";
    return null;
  }, [isMultiMonth, startMonth, startYear, endMonth, endYear]);

  return (
    <Modal isOpen={showLinkModal} toggle={() => setShowLinkModal(false)} scrollable style={{ maxWidth: "600px" }}>
      <ModalHeader toggle={() => setShowLinkModal(false)}>
        {isContrato ? "Vincular cliente" : "Vincular oportunidad"}
      </ModalHeader>
      {clientesLoading ? (
        <ModalBody>
          <div>Cargando clientes...</div>
        </ModalBody>
      ) : (
        <form
          onSubmit={handleSubmitLink(
            async (data) => {
              if (isMultiMonth && rangeError) return;
              if (isMultiMonth && (!startMonth || !endMonth)) {
                setShowMonthsRequired(true);
                return;
              }
              try {
                await handleLinkOportunidad(data, { generatePdfDocument, generateFileName });
              } catch (error) {
                console.error("Error al procesar handleLinkOportunidad desde el formulario:", error);
                toast.error("Error al vincular la oportunidad. Por favor, intenta de nuevo.");
              }
            },
            (errors) => {
              console.log("Errores de validación en el formulario:", errors);
            }
          )}
        >
          <ModalBody>
            {/* --- CLIENTE PUBLICITARIO --- */}
            <div className="mb-2">
              <label htmlFor="clientePublicitario" className="form-label fw-bold">
                Cliente Publicitario
              </label>
              <select
                className={`form-select ${linkErrors.clientePublicitario ? "is-invalid" : ""}`}
                {...registerLink("clientePublicitario", { required: "Debes seleccionar un cliente publicitario" })}
              >
                <option value="">Selecciona un cliente</option>
                {renderClientesOptions()}
              </select>
              {linkErrors.clientePublicitario && (
                <div className="invalid-feedback">{linkErrors.clientePublicitario.message}</div>
              )}
              {creditoLoading && (
                <div style={{ fontSize: "0.78rem", color: "#6c757d", marginTop: "4px" }}>Consultando crédito...</div>
              )}
              {creditoCliente && !creditoLoading && (
                <div
                  style={{
                    marginTop: "6px",
                    padding: "8px 12px",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                    fontSize: "0.82rem",
                    color: "#155724",
                  }}
                >
                  <strong>Crédito disponible:</strong>{" "}
                  {Object.entries(creditoCliente)
                    .map(
                      ([moneda, saldo]) =>
                        `${moneda === "USD" ? "US$" : moneda} ${saldo.toLocaleString(ls, {
                          maximumFractionDigits: moneda === "UF" ? 2 : 0,
                          minimumFractionDigits: moneda === "UF" ? 2 : 0,
                        })}`
                    )
                    .join(" | ")}
                </div>
              )}
            </div>

            {/* --- TIPO COTIZACIÓN --- */}
            <div className="mb-2 d-flex align-items-center">
              <label className="form-label fw-bold mb-0" style={{ width: "50%" }}>
                ¿A quién va dirigida?
              </label>
              <div
                className="d-flex align-items-center"
                style={{
                  width: "50%",
                  background: "#e9ecef",
                  borderRadius: "6px",
                  padding: "2px",
                  cursor: "pointer",
                  fontSize: "0.78rem",
                }}
              >
                <span
                  onClick={() => {
                    setTipoCotizacion("cliente");
                    setLinkValue("tipoCotizacion", "cliente");
                  }}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "4px 0",
                    borderRadius: "5px",
                    fontWeight: tipoCotizacion === "cliente" ? 600 : 400,
                    background: tipoCotizacion === "cliente" ? "#fff" : "transparent",
                    boxShadow: tipoCotizacion === "cliente" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                    transition: "all 0.15s",
                  }}
                >
                  Cliente Directo
                </span>
                <span
                  onClick={() => {
                    setTipoCotizacion("agencia");
                    setLinkValue("tipoCotizacion", "agencia");
                  }}
                  style={{
                    flex: 1,
                    textAlign: "center",
                    padding: "4px 0",
                    borderRadius: "5px",
                    fontWeight: tipoCotizacion === "agencia" ? 600 : 400,
                    background: tipoCotizacion === "agencia" ? "#fff" : "transparent",
                    boxShadow: tipoCotizacion === "agencia" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                    transition: "all 0.15s",
                  }}
                >
                  Agencia
                </span>
              </div>
              <input type="hidden" {...registerLink("tipoCotizacion", { required: "Debes seleccionar una opción" })} />
            </div>

            {/* --- CAMPAÑA --- */}
            <div className="mb-2">
              <label htmlFor="campana" className="form-label fw-bold">
                Campaña asociada (opcional)
              </label>
              <input
                type="text"
                className={`form-control ${linkErrors.campana ? "is-invalid" : ""}`}
                maxLength={15}
                {...registerLink("campana", {
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase().trimEnd();
                  },
                  maxLength: {
                    value: 15,
                    message: "La 'campaña' no puede exceder los 15 caracteres",
                  },
                  validate: (value) => value.trimEnd() === value || "No se permiten espacios al final",
                })}
              />
              {linkErrors.campana && <div className="invalid-feedback">{linkErrors.campana.message}</div>}
            </div>

            {/* --- CANAL + STATUS (mismo row) --- */}
            <div className="mb-2 d-flex gap-3">
              <div style={{ width: "50%" }}>
                <label htmlFor="canal" className="form-label fw-bold">
                  Canal
                </label>
                <select
                  className={`form-select ${linkErrors.canal ? "is-invalid" : ""}`}
                  {...registerLink("canal", { required: "Debes seleccionar un canal" })}
                >
                  <option value="">Canal...</option>
                  <option value="B2C">B2C</option>
                  <option value="B2B">B2B</option>
                  <option value="B2G">B2G</option>
                </select>
                {linkErrors.canal && <div className="invalid-feedback">{linkErrors.canal.message}</div>}
              </div>
              <div style={{ width: "50%" }}>
                <label htmlFor="estatus" className="form-label fw-bold">
                  Status
                </label>
                <select
                  className={`form-select ${linkErrors.estatus ? "is-invalid" : ""}`}
                  {...registerLink("estatus", { required: "Debes seleccionar un estatus" })}
                >
                  <option value="">Status...</option>
                  <option value="100%">100%</option>
                  <option value="90%">90%</option>
                  <option value="75%">75%</option>
                  <option value="50%">50%</option>
                </select>
                {linkErrors.estatus && <div className="invalid-feedback">{linkErrors.estatus.message}</div>}
              </div>
            </div>

            {/* --- CIERRE + FACTURA (month pickers, same row) --- */}
            <div className="mb-2 d-flex gap-3">
              <div style={{ width: isMultiMonth ? "100%" : "50%" }}>
                <label className="form-label fw-bold">Cierre</label>
                <MonthYearPicker
                  value={anoCierre && mesCierre ? `${anoCierre}-${String(mesCierre).padStart(2, "0")}` : ""}
                  onChange={(val) => {
                    const [y, m] = val.split("-");
                    setLinkValue("mes_cierre", String(parseInt(m)));
                    setLinkValue("ano_cierre", y);
                  }}
                  isInvalid={!!(linkErrors.mes_cierre || linkErrors.ano_cierre)}
                />
                <input
                  type="hidden"
                  {...registerLink("mes_cierre", {
                    required: "Selecciona fecha de cierre",
                    validate: (value, formValues) => {
                      if (!value) return true;
                      const mm = formValues.is_multi_month;
                      const mf = mm ? formValues.multi_month_start_month : formValues.mes_factura;
                      const af = mm ? formValues.multi_month_start_year : formValues.ano_factura;
                      if (!mf) return true;
                      const cFull = `${formValues.ano_cierre}-${value.padStart(2, "0")}`;
                      const fFull = `${af}-${mf.padStart(2, "0")}`;
                      return new Date(cFull) <= new Date(fFull) || "Cierre no puede ser posterior a factura";
                    },
                  })}
                />
                <input type="hidden" {...registerLink("ano_cierre", { required: "Selecciona fecha de cierre" })} />
                {(linkErrors.mes_cierre || linkErrors.ano_cierre) && (
                  <div className="text-danger small mt-1">
                    {linkErrors.mes_cierre?.message || linkErrors.ano_cierre?.message}
                  </div>
                )}
              </div>
              {!isMultiMonth && (
                <div style={{ width: "50%" }}>
                  <label className="form-label fw-bold">Factura</label>
                  <MonthYearPicker
                    value={anoFactura && mesFactura ? `${anoFactura}-${String(mesFactura).padStart(2, "0")}` : ""}
                    onChange={(val) => {
                      const [y, m] = val.split("-");
                      setLinkValue("mes_factura", String(parseInt(m)));
                      setLinkValue("ano_factura", y);
                    }}
                    isInvalid={!!(linkErrors.mes_factura || linkErrors.ano_factura)}
                  />
                  <input
                    type="hidden"
                    {...registerLink("mes_factura", {
                      required: !isMultiMonth ? "Selecciona fecha de factura" : false,
                      validate: (value, formValues) => {
                        if (formValues.is_multi_month) return true;
                        if (!value || !formValues.mes_cierre) return true;
                        const cFull = `${formValues.ano_cierre}-${formValues.mes_cierre.padStart(2, "0")}`;
                        const fFull = `${formValues.ano_factura}-${value.padStart(2, "0")}`;
                        return new Date(cFull) <= new Date(fFull) || "Factura no puede ser anterior a cierre";
                      },
                    })}
                  />
                  <input
                    type="hidden"
                    {...registerLink("ano_factura", {
                      required: !isMultiMonth ? "Selecciona fecha de factura" : false,
                    })}
                  />
                  {(linkErrors.mes_factura || linkErrors.ano_factura) && (
                    <div className="text-danger small mt-1">
                      {linkErrors.mes_factura?.message || linkErrors.ano_factura?.message}
                    </div>
                  )}
                </div>
              )}
            </div>

            <hr className="my-2" />

            {/* ============================================================ */}
            {/* TOGGLE: ACUERDO MULTI-MES                                    */}
            {/* ============================================================ */}
            <div className="mb-2">
              <div className="mb-2 d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  id="is_multi_month"
                  checked={isMultiMonth}
                  onChange={(e) => {
                    setLinkValue("is_multi_month", e.target.checked);
                    if (!e.target.checked) {
                      setLinkValue("distribucion_tipo", "prorrateo");
                      setLinkValue("plazo_modo", "mes_tipo");
                      setLinkValue("multi_month_start_month", "");
                      setLinkValue("multi_month_start_year", new Date().getFullYear().toString());
                      setLinkValue("multi_month_end_month", "");
                      setLinkValue("multi_month_end_year", new Date().getFullYear().toString());
                    }
                  }}
                />
                <label htmlFor="is_multi_month" className="fw-bold mb-0">
                  Acuerdo multi-mes
                </label>
              </div>

              {isMultiMonth && (
                <div className="ms-3 ps-2 border-start border-primary border-2">
                  {/* Plazo cotizado toggle */}
                  <div className="mb-2 d-flex align-items-center">
                    <label className="form-label small mb-0" style={{ width: "50%" }}>
                      Plazo cotizado
                    </label>
                    <div
                      className="d-flex align-items-center"
                      style={{
                        width: "50%",
                        background: "#e9ecef",
                        borderRadius: "6px",
                        padding: "2px",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      <span
                        onClick={() => setLinkValue("plazo_modo", "mes_tipo")}
                        style={{
                          flex: 1,
                          textAlign: "center",
                          padding: "3px 0",
                          borderRadius: "5px",
                          fontWeight: plazoModo === "mes_tipo" ? 500 : 400,
                          background: plazoModo === "mes_tipo" ? "#fff" : "transparent",
                          boxShadow: plazoModo === "mes_tipo" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.15s",
                        }}
                      >
                        Por mes
                      </span>
                      <span
                        onClick={() => setLinkValue("plazo_modo", "total")}
                        style={{
                          flex: 1,
                          textAlign: "center",
                          padding: "3px 0",
                          borderRadius: "5px",
                          fontWeight: plazoModo === "total" ? 500 : 400,
                          background: plazoModo === "total" ? "#fff" : "transparent",
                          boxShadow: plazoModo === "total" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.15s",
                        }}
                      >
                        Total
                      </span>
                    </div>
                  </div>

                  {/* Facturación toggle */}
                  <div className="mb-2 d-flex align-items-center">
                    <label className="form-label small mb-0" style={{ width: "50%" }}>
                      Facturación
                    </label>
                    <div
                      className="d-flex align-items-center"
                      style={{
                        width: "50%",
                        background: "#e9ecef",
                        borderRadius: "6px",
                        padding: "2px",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      <span
                        onClick={() => setLinkValue("distribucion_tipo", "prorrateo")}
                        style={{
                          flex: 1,
                          textAlign: "center",
                          padding: "3px 0",
                          borderRadius: "5px",
                          fontWeight: distribucionTipo === "prorrateo" ? 500 : 400,
                          background: distribucionTipo === "prorrateo" ? "#fff" : "transparent",
                          boxShadow: distribucionTipo === "prorrateo" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.15s",
                        }}
                      >
                        Prorrateo
                      </span>
                      <span
                        onClick={() => setLinkValue("distribucion_tipo", "manual")}
                        style={{
                          flex: 1,
                          textAlign: "center",
                          padding: "3px 0",
                          borderRadius: "5px",
                          fontWeight: distribucionTipo === "manual" ? 500 : 400,
                          background: distribucionTipo === "manual" ? "#fff" : "transparent",
                          boxShadow: distribucionTipo === "manual" ? "0 1px 3px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.15s",
                        }}
                      >
                        Manual
                      </span>
                    </div>
                  </div>

                  {/* Rango de meses de facturación */}
                  <div className="mb-2 d-flex gap-3">
                    <div style={{ width: "50%" }}>
                      <label className="form-label small fw-bold mb-1">Primer mes facturable</label>
                      <MonthYearPicker
                        value={startMonth && startYear ? `${startYear}-${String(startMonth).padStart(2, "0")}` : ""}
                        onChange={(val) => {
                          const [y, m] = val.split("-");
                          setLinkValue("multi_month_start_month", String(parseInt(m)));
                          setLinkValue("multi_month_start_year", y);
                          setLinkValue("mes_factura", String(parseInt(m)));
                          setLinkValue("ano_factura", y);
                        }}
                      />
                    </div>
                    <div style={{ width: "50%" }}>
                      <label className="form-label small fw-bold mb-1">Último mes facturable</label>
                      <MonthYearPicker
                        value={endMonth && endYear ? `${endYear}-${String(endMonth).padStart(2, "0")}` : ""}
                        onChange={(val) => {
                          const [y, m] = val.split("-");
                          setLinkValue("multi_month_end_month", String(parseInt(m)));
                          setLinkValue("multi_month_end_year", y);
                        }}
                      />
                    </div>
                  </div>

                  {/* Error de rango */}
                  {rangeError && <div className="text-danger small mb-2">{rangeError}</div>}
                  {showMonthsRequired && (!startMonth || !endMonth) && (
                    <div className="text-danger small mb-2">
                      Selecciona los meses de facturación (Primer y Último mes)
                    </div>
                  )}

                  {/* Rango de meses de pauta */}
                  <div className="mb-2 d-flex gap-3">
                    <div style={{ width: "50%" }}>
                      <label className="form-label small fw-bold mb-1">Inicio pauta</label>
                      <MonthYearPicker
                        value={
                          pautaStartMonth && pautaStartYear
                            ? `${pautaStartYear}-${String(pautaStartMonth).padStart(2, "0")}`
                            : ""
                        }
                        onChange={(val) => {
                          const [y, m] = val.split("-");
                          setLinkValue("pauta_start_month", String(parseInt(m)));
                          setLinkValue("pauta_start_year", y);
                        }}
                      />
                    </div>
                    <div style={{ width: "50%" }}>
                      <label className="form-label small fw-bold mb-1">Fin pauta</label>
                      <MonthYearPicker
                        value={
                          pautaEndMonth && pautaEndYear
                            ? `${pautaEndYear}-${String(pautaEndMonth).padStart(2, "0")}`
                            : ""
                        }
                        onChange={(val) => {
                          const [y, m] = val.split("-");
                          setLinkValue("pauta_end_month", String(parseInt(m)));
                          setLinkValue("pauta_end_year", y);
                        }}
                      />
                    </div>
                  </div>

                  {/* Preview resumen */}
                  {prorateoPreview && (
                    <div className="alert alert-info py-2 small mb-0">
                      {prorateoPreview.mode === "mes_tipo" ? (
                        <>
                          {plazo ? <>{plazo} días/mes &middot; </> : null}
                          <strong>{prorateoPreview.perMonth.toLocaleString(ls)}/mes</strong> &times;{" "}
                          <strong>{prorateoPreview.months} meses</strong> ={" "}
                          <strong>Total: {prorateoPreview.grandTotal.toLocaleString(ls)}</strong>
                        </>
                      ) : (
                        <>
                          {plazo ? <>{plazo} días total &middot; </> : null}
                          <strong>Total: {prorateoPreview.grandTotal.toLocaleString(ls)}</strong> &divide;{" "}
                          <strong>{prorateoPreview.months} meses</strong> ={" "}
                          <strong>{prorateoPreview.perMonth.toLocaleString(ls)}/mes</strong>
                        </>
                      )}
                    </div>
                  )}

                  {/* Info montos manuales */}
                  {distribucionTipo === "manual" && startMonth && endMonth && !rangeError && (
                    <div className="alert alert-warning py-2 small mb-0">
                      Podrás definir montos distintos por mes desde el detalle del contrato.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ============================================================ */}
            {/* TOGGLE: AUDIENCIA DE REFERENCIA                              */}
            {/* ============================================================ */}
            <div className="mb-2">
              <div className="mb-2 d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  id="audiencia_referencia"
                  checked={isAudienciaRef}
                  onChange={(e) => {
                    setLinkValue("audiencia_referencia", e.target.checked);
                    if (e.target.checked && audienciaSeleccionadaTotal) {
                      setLinkValue("audiencia_contratada", Math.round(audienciaSeleccionadaTotal).toLocaleString(ls));
                    }
                    if (!e.target.checked) {
                      setLinkValue("audiencia_contratada", "");
                    }
                  }}
                />
                <label htmlFor="audiencia_referencia" className="fw-bold mb-0">
                  Audiencia de referencia
                </label>
              </div>

              {isAudienciaRef && (
                <div className="ms-3 ps-2 border-start border-warning border-2">
                  <div className="alert alert-warning py-2 small mb-2">
                    Los edificios cotizados son orientativos. El cliente seleccionará inventario libremente hasta cubrir
                    la audiencia contratada.
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <label className="form-label small mb-0 fw-bold" style={{ minWidth: "170px" }}>
                      Personas comprometidas/mes:
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control form-control-sm"
                      style={{ maxWidth: "140px" }}
                      {...registerLink("audiencia_contratada", {
                        validate: (value) => {
                          if (!isAudienciaRef) return true;
                          const num = parseInt(String(value).replace(/[\s,.]/g, ""));
                          if (!num || num <= 0) return "Ingresa la audiencia comprometida";
                          return true;
                        },
                        onChange: (e) => {
                          const raw = e.target.value.replace(/\D/g, "");
                          if (raw) {
                            e.target.value = parseInt(raw).toLocaleString(ls);
                          }
                        },
                      })}
                    />
                  </div>
                  {linkErrors.audiencia_contratada && (
                    <div className="text-danger small mt-1">{linkErrors.audiencia_contratada.message}</div>
                  )}
                </div>
              )}
            </div>

            {/* ============================================================ */}
            {/* USAR CRÉDITO DISPONIBLE                                      */}
            {/* ============================================================ */}
            {(() => {
              const monedaCotizacion = forceDollars ? "USD" : pais === "Peru" ? "PEN" : "CLP";
              const saldoDisponible = creditoCliente ? creditoCliente[monedaCotizacion] || 0 : 0;
              if (!creditoCliente || creditoLoading || saldoDisponible <= 0) return null;

              const monedaLabel = monedaCotizacion === "USD" ? "US$" : monedaCotizacion;
              const montoIngresado = parseFloat(watchLink ? watchLink("monto_credito") : 0) || 0;
              const excedente = totalImporte - montoIngresado;

              return (
                <div
                  className="mb-2"
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "6px",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      id="usar_credito"
                      {...registerLink("usar_credito")}
                      onChange={(e) => {
                        registerLink("usar_credito").onChange(e);
                        if (e.target.checked) {
                          const autoMonto = Math.min(saldoDisponible, totalImporte);
                          setLinkValue("monto_credito", autoMonto);
                        } else {
                          setLinkValue("monto_credito", "");
                        }
                      }}
                    />
                    <label htmlFor="usar_credito" className="fw-bold mb-0" style={{ fontSize: "0.85rem" }}>
                      Usar crédito disponible ({monedaLabel} {saldoDisponible.toLocaleString(ls)})
                    </label>
                  </div>
                  {watchLink && watchLink("usar_credito") && (
                    <div className="ms-3 ps-2 border-start border-success border-2 mt-2">
                      <div className="d-flex align-items-center gap-2">
                        <label className="form-label small mb-0 fw-bold" style={{ minWidth: "130px" }}>
                          Monto a descontar:
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ maxWidth: "160px" }}
                          {...registerLink("monto_credito", {
                            validate: (value) => {
                              if (!watchLink("usar_credito")) return true;
                              const num = parseFloat(value);
                              if (!num || num <= 0) return "Ingresa un monto válido";
                              if (num > saldoDisponible)
                                return `Máximo disponible: ${saldoDisponible.toLocaleString(ls)}`;
                              return true;
                            },
                          })}
                        />
                      </div>
                      {linkErrors.monto_credito && (
                        <div className="text-danger small mt-1">{linkErrors.monto_credito.message}</div>
                      )}
                      {montoIngresado > 0 && (
                        <div
                          className="small mt-2"
                          style={{
                            padding: "6px 10px",
                            backgroundColor: excedente > 0 ? "#fff3cd" : "#d4edda",
                            borderRadius: "4px",
                            fontSize: "0.78rem",
                          }}
                        >
                          {excedente > 0 ? (
                            <>
                              Crédito: {monedaLabel} {montoIngresado.toLocaleString(ls)} | Venta nueva:{" "}
                              <strong>
                                {monedaLabel} {excedente.toLocaleString(ls)}
                              </strong>
                            </>
                          ) : excedente === 0 ? (
                            <strong>Crédito cubre el 100% — no se genera venta</strong>
                          ) : (
                            <>
                              <strong>Crédito cubre el 100% — no se genera venta</strong> | Saldo restante:{" "}
                              {monedaLabel} {Math.abs(excedente).toLocaleString(ls)}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowLinkModal(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={!canLinkOpportunity || isSubmitting || (isMultiMonth && !!rangeError)}
            >
              {isSubmitting ? "Procesando..." : isContrato ? "Vincular Cliente" : "Vincular"}
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  );
}
